import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ScrollReveal } from "@/components/ScrollReveal";
import { SERVICE_TYPES, LOCATIONS, BRAND } from "@/data/content";
import { Loader2, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  name: "",
  phone: "",
  email: "",
  service_type: SERVICE_TYPES[0],
  centre: LOCATIONS[0].name,
  preferred_date: "",
  message: "",
};

const fieldClass =
  "w-full mt-2 border-0 border-b border-slate-300 px-0 py-3 bg-transparent focus:outline-none focus:border-teal text-navy placeholder:text-slate-400 transition-colors duration-300 hover:border-navy/40";

export const BookingForm = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success("Request received! Our team will call you within 30 minutes.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" className="relative bg-white py-24 lg:py-32 overflow-hidden" data-testid="booking-section">
      <div className="absolute inset-0 glow-soft pointer-events-none" />
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 relative grid lg:grid-cols-[0.8fr_1.2fr] gap-14">
        <ScrollReveal>
          <span className="text-teal text-xs uppercase tracking-[0.3em] font-semibold">
            Book Now
          </span>
          <h2 className="font-display text-4xl lg:text-5xl text-navy mt-3">
            Tell us what you need. We'll take it from there.
          </h2>
          <p className="text-slate-600 leading-relaxed mt-5 max-w-sm text-sm">
            Fill in the details and our care team will call you back to confirm
            your slot — no payment required to book. For urgent needs, call{" "}
            <a href={`tel:${BRAND.phoneMain.replace(/\s/g, "")}`} className="text-teal">
              {BRAND.phoneMain}
            </a>{" "}
            directly.
          </p>
        </ScrollReveal>

        <ScrollReveal delay={0.1}>
          <form
            onSubmit={handleSubmit}
            className="bg-gradient-to-br from-cream via-white to-cream border border-navy/8 p-8 lg:p-12 space-y-7 shadow-[0_30px_70px_rgba(15,31,61,0.08)]"
            data-testid="booking-form"
          >
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-slate-500">Full Name</label>
                <input
                  data-testid="booking-input-name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className={fieldClass}
                  placeholder="Your full name"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-slate-500">Phone Number</label>
                <input
                  data-testid="booking-input-phone"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className={fieldClass}
                  placeholder="+91 XXXXX XXXXX"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-slate-500">Email (Optional)</label>
                <input
                  data-testid="booking-input-email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className={fieldClass}
                  placeholder="you@email.com"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-slate-500">Preferred Date</label>
                <input
                  data-testid="booking-input-date"
                  type="date"
                  name="preferred_date"
                  value={form.preferred_date}
                  onChange={handleChange}
                  className={fieldClass}
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-slate-500">Service Needed</label>
                <select
                  data-testid="booking-select-service"
                  name="service_type"
                  value={form.service_type}
                  onChange={handleChange}
                  className={fieldClass}
                >
                  {SERVICE_TYPES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-slate-500">Preferred Centre</label>
                <select
                  data-testid="booking-select-centre"
                  name="centre"
                  value={form.centre}
                  onChange={handleChange}
                  className={fieldClass}
                >
                  {LOCATIONS.map((l) => (
                    <option key={l.name} value={l.name}>{l.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-500">Message (Optional)</label>
              <textarea
                data-testid="booking-input-message"
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={3}
                className={`${fieldClass} resize-none`}
                placeholder="Anything specific we should know?"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="booking-submit-button"
              className="btn-sheen w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-navy text-white px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-teal transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              Submit Request
            </button>
          </form>
        </ScrollReveal>
      </div>
    </section>
  );
};
